import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Normalize phone across:
 * - whatsapp:+1415...
 * - web:1310...
 * - +1 (310) 962-...
 * - 1310...
 * Strategy: keep digits only, accept >= 8 digits.
 */
function normalizePhoneAny(raw) {
  const s = String(raw || '')
    .replace(/^whatsapp:/i, '')
    .replace(/^web:/i, '')
    .trim();

  const digits = s.replace(/[^\d]/g, '');
  if (digits.length < 8) return '';
  return digits;
}

function getMessageVisitorName(msg) {
  const a = String(msg?.visitorName || '').trim();
  if (a) return a;

  const b = String(msg?.meta?.visitor?.name || '').trim();
  if (b) return b;

  // optional fallbacks if you ever store these differently
  const c = String(msg?.meta?.visitorName || '').trim();
  if (c) return c;

  return '';
}

const History = () => {
  const [conversations, setConversations] = useState({});
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchConversations();
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/messages`, {
        headers: getAuthHeaders(),
      });

      const grouped = groupBySenderNormalized(res.data);
      setConversations(grouped);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/clients`, {
        headers: getAuthHeaders(),
      });
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const groupBySenderNormalized = (data) => {
    const grouped = {};
    (data || []).forEach((msg) => {
      const key = normalizePhoneAny(msg?.sender);
      const fallbackKey = key || String(msg?.sender || 'unknown').trim() || 'unknown';

      if (!grouped[fallbackKey]) grouped[fallbackKey] = [];
      grouped[fallbackKey].push(msg);
    });

    // keep messages ordered by receivedAt asc inside each thread (optional but nice)
    Object.keys(grouped).forEach((k) => {
      grouped[k].sort((a, b) => {
        const da = new Date(a?.receivedAt || a?.createdAt || 0).getTime();
        const db = new Date(b?.receivedAt || b?.createdAt || 0).getTime();
        return da - db;
      });
    });

    return grouped;
  };

  const clientIndex = useMemo(() => {
    const map = new Map();
    (clients || []).forEach((c) => {
      const p = normalizePhoneAny(c?.phone);
      if (p) map.set(p, c);
    });
    return map;
  }, [clients]);

  const getClient = (senderKey) => {
    const phone = normalizePhoneAny(senderKey);
    if (!phone) return null;
    return clientIndex.get(phone) || null;
  };

  const handleMarkAttended = async (senderKey) => {
    try {
      const phone = normalizePhoneAny(senderKey);
      if (!phone) {
        alert('Invalid phone for this conversation');
        return;
      }

      await axios.post(
        `${API_URL}/messages/mark_attended`,
        { phone },
        { headers: getAuthHeaders() }
      );

      await fetchClients();
      await fetchConversations();
    } catch (err) {
      alert('Failed to mark as attended');
      console.error(err);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US');
  };

  return (
    <div className="px-6 py-4">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">📨 Conversation History</h2>

      <div className="space-y-4">
        {Object.entries(conversations).map(([senderKey, messages]) => {
          const client = getClient(senderKey);
          const isAwaiting = client?.awaitingHuman;

          // Best display name:
          // 1) client.name
          // 2) first message with visitorName/meta.visitor.name
          // 3) Unknown
          const nameFromMsgs =
            (messages || []).map(getMessageVisitorName).find((x) => !!x) || '';

          const displayName = String(client?.name || '').trim() || nameFromMsgs || 'Unknown';

          // Show a readable phone (digits) if we have it, else original key
          const phoneDigits = normalizePhoneAny(senderKey);
          const displayPhone = phoneDigits ? phoneDigits : String(senderKey || '').trim();

          return (
            <details key={senderKey} className="bg-white rounded shadow">
              <summary className="cursor-pointer select-none px-4 py-3 bg-gray-100 flex justify-between items-center text-sm font-semibold">
                <div className="flex flex-col">
                  <span className="text-gray-800">
                    👤 {displayName} - {displayPhone}
                  </span>
                  <span className="text-xs text-gray-500">{messages.length} message(s)</span>
                </div>

                <div className="flex items-center gap-2">
                  {isAwaiting && (
                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                      🚨 Awaiting Human
                    </span>
                  )}
                  <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                </div>
              </summary>

              <div className="px-6 py-4 space-y-4">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex flex-col ${msg?.origin === 'client' ? 'items-start' : 'items-end'}`}
                  >
                    <div
                      className={`rounded-lg px-4 py-2 shadow max-w-xl text-sm ${
                        msg?.origin === 'client'
                          ? 'bg-gray-200 text-gray-900'
                          : 'bg-blue-600 text-white'
                      }`}
                    >
                      <p>
                        <strong>Message:</strong> {msg?.message}
                      </p>

                      {msg?.iaReply && (
                        <p className="mt-1">
                          <strong>AI Reply:</strong> {msg?.iaReply}
                        </p>
                      )}

                      <p className="text-xs text-gray-400 mt-1">
                        {formatDate(msg?.receivedAt || msg?.createdAt)}
                      </p>
                    </div>
                  </div>
                ))}

                {isAwaiting && (
                  <div className="mt-4">
                    <button
                      className="text-sm px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      onClick={() => handleMarkAttended(senderKey)}
                    >
                      ✔️ Mark as attended
                    </button>
                  </div>
                )}
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
};

export default History;
